import { Fragment } from '@tiptap/pm/model'
import { TextSelection } from '@tiptap/pm/state'

// RED-317 §9 — a suggestion (plain paragraphs, e.g. the AI draft) taken into
// the Studio document so that it looks like the text around it. Every new
// paragraph is a `paragraph` node with a template paragraph's verbatim pPr
// (plus its styleId and alignment delta) and the marks of that paragraph's
// dominant run, the one holding the most text: its ooxmlRun rPr verbatim and
// the toggles (bold, italic, …) that rPr implies, so the compiler re-emits
// both byte for byte (pr3-contract.md §1, §2). A bold lead-in does not make
// the whole suggestion bold. Links are not carried over.
//
// "Asenda valik" replaces the selected text: the unselected start of the
// first paragraph and end of the last one stay where they are. The template
// is the first replaced `paragraph`. "Lisa kursori kohale" inserts at the
// cursor: after its paragraph at the end, before it at the start, in place of
// it when it is empty, else between the two halves of a split. The template
// is the cursor's paragraph, or the nearest paragraph with text when the
// cursor sits in a heading or an empty line. Both work only between
// paragraphs and headings of one container (the page, one table cell, one
// list item): a selection across a table or a chart card is refused.
//
// "Võta ettepanek üle" takes a whole suggested text in (the AI draft's
// `blocks`, built on the server by MarketOverviewCarryOver): every carried
// block is the editor's own node at that index, every AI paragraph a new one
// styled after the node its `style` index names. It runs only while the
// editor still holds the version the suggestion was built on: the same
// number of blocks, each with the text in `fingerprints` (blockText(), the
// server's MarketOverviewCarryOver::text()).

const TEXTBLOCKS = new Set(['paragraph', 'heading'])
const COPIED_ATTRS = ['pPr', 'styleId', 'textAlign']
const DROPPED_MARKS = new Set(['link'])

/** The marks of the paragraph's run with the most text (none for an empty paragraph). */
function dominantMarks(paragraph) {
  let longest = null
  paragraph.forEach(child => {
    if (child.isText && (longest === null || child.text.length > longest.text.length)) {
      longest = child
    }
  })
  return longest ? longest.marks.filter(mark => !DROPPED_MARKS.has(mark.type.name)) : []
}

const hasText = node => node.type.name === 'paragraph' && node.textContent.trim() !== ''

/** The paragraph nodes of `texts`, styled after `template` (a paragraph node, or null for the defaults). */
export function suggestionNodes(schema, texts, template) {
  const attrs = template ? Object.fromEntries(COPIED_ATTRS.map(name => [name, template.attrs[name] ?? null])) : {}
  const marks = template ? dominantMarks(template) : []
  return texts
    .map(text => String(text ?? '').trim())
    .filter(text => text !== '')
    .map(text => schema.nodes.paragraph.create(attrs, schema.text(text, marks)))
}

/**
 * The blocks between $from and $to, when both sit in paragraphs or headings
 * of the same container and every block between them is one too; else null.
 */
function textRange(doc, from, to) {
  const $from = doc.resolve(from)
  const $to = doc.resolve(to)
  if (!$from.parent.isTextblock || !$to.parent.isTextblock || $from.depth !== $to.depth) {
    return null
  }
  const depth = $from.depth
  if ($from.start(depth - 1) !== $to.start(depth - 1)) {
    return null
  }
  const container = $from.node(depth - 1)
  const blocks = []
  for (let index = $from.index(depth - 1); index <= $to.index(depth - 1); index++) {
    const block = container.child(index)
    if (!TEXTBLOCKS.has(block.type.name)) {
      return null
    }
    blocks.push(block)
  }
  return { $from, $to, depth, blocks }
}

/** The nearest paragraph with text before `pos`, else after it; null in a document without one. */
function nearestParagraph(doc, pos) {
  let before = null
  let after = null
  doc.descendants((node, nodePos) => {
    if (hasText(node)) {
      if (nodePos < pos) {
        before = node
      } else if (after === null) {
        after = node
      }
    }
    return node.isBlock && !node.isTextblock
  })
  return before ?? after
}

/** Why "Asenda valik" cannot run now: null when it can, 'empty' (nothing selected) or 'structure'. */
export function replaceRefusal(state) {
  const { from, to, empty } = state.selection
  if (empty) {
    return 'empty'
  }
  return textRange(state.doc, from, to) === null ? 'structure' : null
}

/** Why "Lisa kursori kohale" cannot run now: null when it can, 'structure' when the cursor is on a card or table. */
export function insertRefusal(state) {
  const { to } = state.selection
  return textRange(state.doc, to, to) === null ? 'structure' : null
}

/** Replace $from…$to (inside `range`) by left + nodes + right in one step; the cursor lands after the last new paragraph. */
function place(tr, range, nodes) {
  const { $from, $to, depth } = range
  const left = $from.parent.cut(0, $from.parentOffset)
  const right = $to.parent.cut($to.parentOffset)
  const pieces = [...(left.content.size > 0 ? [left] : []), ...nodes, ...(right.content.size > 0 ? [right] : [])]
  const start = $from.before(depth)
  tr.replaceWith(start, $to.after(depth), Fragment.from(pieces))
  const before = left.content.size > 0 ? left.nodeSize : 0
  const end = start + before + nodes.reduce((size, node) => size + node.nodeSize, 0) - 1
  tr.setSelection(TextSelection.create(tr.doc, end)).scrollIntoView()
  return true
}

/** "Asenda valik": the selected text gives way to the suggestion. False (nothing changed) when replaceRefusal() says why. */
export function replaceSelectionWithSuggestion(editor, texts) {
  return editor.commands.command(({ tr, state, dispatch }) => {
    if (replaceRefusal(state) !== null) {
      return false
    }
    const range = textRange(state.doc, state.selection.from, state.selection.to)
    const template = range.blocks.find(block => block.type.name === 'paragraph') ?? null
    const nodes = suggestionNodes(state.schema, texts, template)
    if (nodes.length === 0) {
      return false
    }
    return dispatch ? place(tr, range, nodes) : true
  })
}

/** "Lisa kursori kohale": the suggestion goes in at the cursor. False (nothing changed) when insertRefusal() says why. */
export function insertSuggestionAtCursor(editor, texts) {
  return editor.commands.command(({ tr, state, dispatch }) => {
    if (insertRefusal(state) !== null) {
      return false
    }
    const { to } = state.selection
    const range = textRange(state.doc, to, to)
    const cursorParagraph = range.blocks[0]
    const template = hasText(cursorParagraph) ? cursorParagraph : (nearestParagraph(state.doc, to) ?? (cursorParagraph.type.name === 'paragraph' ? cursorParagraph : null))
    const nodes = suggestionNodes(state.schema, texts, template)
    if (nodes.length === 0) {
      return false
    }
    return dispatch ? place(tr, range, nodes) : true
  })
}

/** A block's text: its text nodes joined, inline content as is, blocks with a space (MarketOverviewCarryOver::text()). */
export function blockText(node) {
  if (node.isText) {
    return node.text
  }
  const parts = []
  node.forEach(child => {
    const text = blockText(child)
    if (text !== '') {
      parts.push(text)
    }
  })
  return parts.join(TEXTBLOCKS.has(node.type.name) ? '' : ' ')
}

/** Why "Võta ettepanek üle" cannot run now: null when it can, 'none' (no version to build on) or 'changed' (the draft is no longer that version). */
export function applyRefusal(state, fingerprints) {
  if (!Array.isArray(fingerprints)) {
    return 'none'
  }
  const { doc } = state
  if (doc.childCount !== fingerprints.length) {
    return 'changed'
  }
  for (let index = 0; index < doc.childCount; index++) {
    if (blockText(doc.child(index)) !== fingerprints[index]) {
      return 'changed'
    }
  }
  return null
}

/** "Võta ettepanek üle": the document becomes the suggested text. False (nothing changed) when applyRefusal() says why. */
export function applySuggestion(editor, blocks, fingerprints) {
  return editor.commands.command(({ tr, state, dispatch }) => {
    if (applyRefusal(state, fingerprints) !== null || !Array.isArray(blocks) || blocks.length === 0) {
      return false
    }
    const { doc, schema } = state
    let firstNew = null
    const nodes = []
    for (const block of blocks) {
      if (block.kind === 'carried') {
        nodes.push(doc.child(block.index))
        continue
      }
      const styled = Number.isInteger(block.style) && block.style < doc.childCount ? doc.child(block.style) : null
      const template = styled?.type.name === 'paragraph' ? styled : null
      const [paragraph] = suggestionNodes(schema, [block.text], template)
      if (paragraph) {
        firstNew ??= nodes.reduce((size, node) => size + node.nodeSize, 0) + paragraph.nodeSize - 1
        nodes.push(paragraph)
      }
    }
    if (dispatch) {
      tr.replaceWith(0, doc.content.size, Fragment.from(nodes))
      if (firstNew !== null) {
        tr.setSelection(TextSelection.create(tr.doc, firstNew)).scrollIntoView()
      }
    }
    return true
  })
}
